import type { ReactElement } from "react";

import { useUnmount } from "app/custom-hooks/useUnmount";
import type { LeadPriorityScore } from "app/pages/sales-intelligence/domain/leads/types/lead-priority";
import { ScoringRuleWizardModal } from "app/pages/sales-intelligence/modules/lead-scoring-agent/components/ScoringRuleWizardModal";
import { useLeadScoreScoringRuleFlow } from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useLeadScoreScoringRuleFlow";
import { useScoringRuleWizardStore } from "app/pages/sales-intelligence/modules/lead-scoring-agent/store/useScoringRuleWizardStore";

import { ScoringVisibilityModal } from "./ScoringVisibilityModal";

type ScoringRuleWizardFlowProps = {
    priority: LeadPriorityScore;
    onCreated: (priority: LeadPriorityScore) => void;
    onClose: () => void;
};

const ScoringRuleWizardFlow = ({ priority, onCreated, onClose }: ScoringRuleWizardFlowProps): ReactElement => {
    const { step, isSaving, isFetching, handleVisibilityNext, handleWizardSave } = useLeadScoreScoringRuleFlow({
        priority,
        onCreated,
        onClose,
    });

    if (step.type === "visibility") {
        return (
            <ScoringVisibilityModal
                isOpen
                onClose={onClose}
                onNext={handleVisibilityNext}
            />
        );
    }

    return (
        <ScoringRuleWizardModal
            key={`${priority}-${step.configType}`}
            isOpen
            mode="create"
            priority={priority}
            configType={step.configType}
            onClose={onClose}
            onSave={handleWizardSave}
            isSaving={isSaving || isFetching}
        />
    );
};

/**
 * Page-root host for the Lead Score scoring-rule flow. It renders the flow only while a rule is pending, so the
 * flow's scoring-config queries mount on wizard-open — never at page load, where they would compete with the
 * filters fetch. Mount this inside the surface's route guards; the trigger lives in the Lead Score filter view.
 */
export const ScoringRuleWizardHost = (): ReactElement | null => {
    const pending = useScoringRuleWizardStore((state) => state.pending);
    const close = useScoringRuleWizardStore((state) => state.close);

    // A pending flow must not survive a route change (UIView remounts the page component on state change).
    useUnmount(close);

    if (!pending) {
        return null;
    }

    return (
        <ScoringRuleWizardFlow
            priority={pending.priority}
            onCreated={pending.onCreated}
            onClose={close}
        />
    );
};
